import { Fragment, useMemo, useRef } from "react"
import { Tooltip, TooltipRefProps } from "react-tooltip"
import classNames from "classnames"
import styled from "styled-components"

import { UITypes } from "types"
import { TableActionButtonGroup } from "types/table/tableActionButton"
import { ReactComponent as IconDelete } from "assets/svg/icon-delete.svg"
import { ReactComponent as IconEdit } from "assets/svg/icon-edit.svg"
import { ReactComponent as IconOptions } from "assets/svg/icon-options.svg"

import styles from "../index.module.scss"

const MobileActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const MobileActionItem = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;

  ${({ disabled }) =>
    disabled &&
    `
      opacity: 0.5;
      cursor: not-allowed;
    `}
`

interface Props {
  tableName?: string
  rowData?: UITypes.TableBodyRow
  isMobileDevice?: boolean
  onDeleteClick?: (id: number) => void
  onEditClick?: (id: number) => void
  getRowActions?: (rowData: UITypes.TableBodyRow) => TableActionButtonGroup
}

export const TableRowNavigation = ({
  tableName,
  rowData,
  isMobileDevice = false,
  onDeleteClick,
  onEditClick,
  getRowActions,
}: Props) => {
  const rowSettings = rowData?.rowSettings
  const tooltipRef = useRef<TooltipRefProps>(null)

  const onDelete = useMemo(() => {
    if (tableName === "vendor-orders" && rowSettings?.status !== "new") {
      return undefined
    }
    if (tableName === "contractor-orders" && rowSettings?.status !== "new") {
      return undefined
    } else if (onDeleteClick) {
      return () => onDeleteClick(rowSettings?.id as number)
    }
  }, [tableName, rowSettings, onDeleteClick])

  const onEdit = useMemo(() => {
    if (
      tableName === "vendor-orders" &&
      rowSettings?.status !== "new" &&
      rowSettings?.status !== "denied" &&
      rowSettings?.status !== "confirmed" &&
      rowSettings?.status !== "pending"
    ) {
      return undefined
    }
    if (tableName === "contractor-orders" && rowSettings?.status !== "new") {
      return undefined
    }
    if (tableName === "customers" && rowSettings?.isEnable !== true) {
      return undefined
    } else if (onEditClick) {
      return () => onEditClick(rowSettings?.id as number)
    }
  }, [tableName, rowSettings, onEditClick])

  const RowActions = useMemo(() => {
    if (getRowActions && rowData) {
      const { className, actionButtons } = getRowActions(rowData)
      const visibleActionButtons = actionButtons.filter(action => !action.hidden)

      if (visibleActionButtons.length === 0) {
        return null
      }

      if (isMobileDevice && visibleActionButtons.length > 1) {
        return () => (
          <>
            <span key={`action-options-${rowSettings?.id}`} data-tooltip-id={`action-options-${rowSettings?.id}`}>
              <IconOptions />
            </span>
          </>
        )
      }

      return () => (
        <div
          className={classNames(styles.tableActionsWrapper, className, {
            [styles.redFlag]: !!rowSettings?.isRedFlag,
          })}
        >
          {actionButtons.map((action, index) => {
            const key = ["action", index].join("-")
            return (
              <Fragment key={key}>
                <span
                  onClick={() => !action.disabled && action.handler?.(rowSettings?.id as number)}
                  className={classNames({
                    [styles.disabledNavButton]: action.disabled,
                    [styles.hidden]: action.hidden,
                    [action.className || ""]: action.className,
                  })}
                  data-tooltip-id={key}
                  role="button"
                  tabIndex={action.hidden || action.disabled ? -1 : 0}
                  aria-label={typeof action.icon === "string" ? action.icon : undefined}
                  aria-hidden={action.hidden}
                >
                  {action.icon}
                </span>
                <Tooltip
                  id={key}
                  place={action.tooltipPosition}
                  variant="dark"
                  classNameArrow={styles.tooltipArrow}
                  className={classNames(styles.tooltip, styles.noBoxShadow)}
                  openOnClick={isMobileDevice}
                  globalCloseEvents={{ clickOutsideAnchor: true }}
                  delayShow={200}
                >
                  {action.tooltip}
                </Tooltip>
              </Fragment>
            )
          })}
        </div>
      )
    }
  }, [getRowActions, rowData, rowSettings])
  const { actionButtons } = getRowActions?.(rowData as UITypes.TableBodyRow) || {}

  return (
    <>
      <div
        className={classNames("rowNavWrapper", styles.rowNavWrapper, styles.rowNavVisibility, {
          [styles.redFlag]: rowSettings?.isRedFlag,
          [styles.mobileDevice]: isMobileDevice,
        })}
      >
        <div
          className={classNames("rowNavButtonsContainer", {
            [styles.rowNavButtonsContainer]: !isMobileDevice,
            [styles.tableActionsWrapper]: isMobileDevice,
            [styles.withRowActions]: !!RowActions,
          })}
        >
          {RowActions && <RowActions />}
          {onEdit && (
            <span className={classNames({ [styles.rowNavButton]: !isMobileDevice })} onClick={onEdit}>
              <IconEdit />
            </span>
          )}
          {onDelete && (
            <span className={classNames({ [styles.rowNavButton]: !isMobileDevice })} onClick={onDelete}>
              <IconDelete />
            </span>
          )}
        </div>
      </div>
      <Tooltip
        id={`action-options-${rowSettings?.id}`}
        place={"bottom-end"}
        variant="light"
        classNameArrow={styles.tooltipArrow}
        className={classNames(styles.mobileTooltip)}
        clickable
        ref={tooltipRef}
        globalCloseEvents={{ clickOutsideAnchor: true }}
      >
        <MobileActionsContainer>
          {actionButtons?.map((action, index) => (
            <MobileActionItem
              disabled={action.disabled}
              key={`action-${index}-${rowSettings?.id}`}
              onClick={() => {
                tooltipRef.current?.close()
                if (!action.disabled && action.handler) {
                  action.handler(rowSettings?.id as number)
                }
              }}
            >
              <span
                className={classNames({
                  [styles.disabledNavButton]: action.disabled,
                  [styles.hidden]: action.hidden,
                })}
                role="button"
                tabIndex={action.hidden || action.disabled ? -1 : 0}
                aria-label={typeof action.icon === "string" ? action.icon : undefined}
                aria-hidden={action.hidden}
              >
                {action.icon}
              </span>
              <div>{action.tooltip}</div>
            </MobileActionItem>
          ))}
        </MobileActionsContainer>
      </Tooltip>
    </>
  )
}
